import api from '../../lib/api';

export type ImportMode = 'fixture' | 'live';
export type ImportOperation = 'index_import' | 'document_acquisition' | 'periodic_refresh';

interface PageWire<T> {
  entries?: T[];
  next_cursor?: string | null;
  has_more?: boolean;
}

export interface SourceHealth {
  status: string;
  checkedAt: string | null;
  lastSuccessfulAt: string | null;
  parseState: string | null;
  failureMessage: string | null;
}

export interface DocumentState {
  selectedUrl: string | null;
  selectedUrls: string[];
  selectedCount: number;
  successfulArtifact: boolean;
  successfulArtifactCount: number;
  state: string;
  eligible: boolean;
  disabledReason: string | null;
}

export interface OperationsSummary {
  currentCycle: string;
  selectedCycle: string;
  runningOperation: ImportRunSummary | null;
  lastSuccessfulLiveRun: ImportRunSummary | null;
  sourceHealth: Record<string, SourceHealth>;
  materialChangeCount: number;
  initiativeCount: number;
  failureCount: number;
  sourceFailureCount: number;
  documentFailureCount: number;
  /** On the ballot by status but not yet published — the review queue (PRD v2 §5.3.5). */
  needsPublishCount: number;
  documentCoverage: Record<string, number>;
  analysisCoverage: Record<string, number>;
}

export interface ImportRunSummary {
  id: string;
  jobId: string | null;
  operation: ImportOperation;
  mode: ImportMode;
  cycle: string;
  coverageScope: string | null;
  status: string;
  startedAt: string;
  completedAt: string | null;
  summary: Record<string, unknown>;
  jobStatus: string | null;
  errorSummary: string | null;
}

export interface ImportRunDetail {
  snapshots: unknown[];
  fetchAttempts: unknown[];
  documents: unknown[];
  warnings: unknown[];
  jobLogs: unknown[];
}

export interface DocumentCoverageEntry {
  initiativeId: string;
  cycle: string;
  initiativeNumber: string;
  measureNumber: string | null;
  popularName: string | null;
  unofficialCaption: string | null;
  ballotTitle: string | null;
  status: string;
  documents: Record<string, DocumentState>;
}

export interface SourceChangeEntry {
  id: string;
  initiativeId: string;
  sourceCode: string;
  fieldPath: string;
  changeType: string;
  beforeValue: unknown;
  afterValue: unknown;
  disposition: string;
  cause: string | null;
  operation: string | null;
  mode: string | null;
  visibility: string;
  sourceUrl: string | null;
  occurredAt: string;
}

export interface BulkPreview {
  eligibleIdentityCount: number;
  manifestSha256: string;
  resumedFromRunId: string | null;
  executionIdentityLimit: number;
  identitiesInNextExecution: number;
  pdfsReused: number;
  pdfsToAcquire: number;
  documentsSelected: number;
}

type UnknownRecord = Record<string, unknown>;

const record = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as UnknownRecord : {};
const text = (value: unknown, fallback = ''): string => typeof value === 'string' ? value : fallback;
const nullableText = (value: unknown): string | null => typeof value === 'string' ? value : null;
const count = (value: unknown): number => Number.isFinite(Number(value)) ? Number(value) : 0;
const boolean = (value: unknown): boolean => value === true;

const normalizeRun = (value: unknown): ImportRunSummary => {
  const wire = record(value);
  return {
    id: text(wire.id),
    jobId: nullableText(wire.job_id),
    operation: text(wire.operation, 'index_import') as ImportOperation,
    mode: text(wire.mode, 'live') as ImportMode,
    cycle: text(wire.initiative_cycle),
    coverageScope: nullableText(wire.coverage_scope),
    status: text(wire.status, 'unknown'),
    startedAt: text(wire.started_at),
    completedAt: nullableText(wire.completed_at),
    summary: record(wire.summary),
    jobStatus: nullableText(wire.job_status),
    errorSummary: nullableText(wire.error_summary),
  };
};

const normalizeSourceHealth = (value: unknown): SourceHealth => {
  const wire = record(value);
  return {
    status: text(wire.status, 'unknown'),
    checkedAt: nullableText(wire.attempted_at),
    lastSuccessfulAt: nullableText(wire.last_successful_at),
    parseState: nullableText(wire.parse_state),
    failureMessage: nullableText(wire.failure_message),
  };
};

const normalizeDocumentState = (value: unknown): DocumentState => {
  const wire = record(value);
  const selectedUrls = Array.isArray(wire.selected_urls)
    ? wire.selected_urls.filter((item): item is string => typeof item === 'string')
    : (nullableText(wire.selected_url) ? [String(wire.selected_url)] : []);
  return {
    selectedUrl: nullableText(wire.selected_url),
    selectedUrls,
    selectedCount: count(wire.selected_count) || selectedUrls.length,
    successfulArtifact: boolean(wire.successful_artifact),
    successfulArtifactCount: count(wire.successful_artifact_count),
    state: text(wire.state, 'missing'),
    eligible: boolean(wire.eligible),
    disabledReason: nullableText(wire.disabled_reason),
  };
};

export const normalizeOperationsSummary = (value: unknown): OperationsSummary => {
  const wire = record(value);
  const sourceWire = record(wire.source_health);
  const coverageWire = record(wire.document_coverage);
  const analysisCoverageWire = record(wire.analysis_coverage);
  const coverage: OperationsSummary['documentCoverage'] = {};
  Object.entries(coverageWire).forEach(([key, item]) => {
    if (key !== 'initiatives_total') coverage[key] = count(item);
  });
  const sourceHealth: Record<string, SourceHealth> = {};
  Object.entries(sourceWire).forEach(([key, item]) => { sourceHealth[key] = normalizeSourceHealth(item); });
  return {
    currentCycle: text(wire.current_cycle),
    selectedCycle: text(wire.selected_cycle),
    runningOperation: wire.running_operation ? normalizeRun(wire.running_operation) : null,
    lastSuccessfulLiveRun: wire.last_successful_live_run ? normalizeRun(wire.last_successful_live_run) : null,
    sourceHealth,
    materialChangeCount: count(record(wire.material_changes).latest_run_count),
    initiativeCount: count(coverageWire.initiatives_total),
    failureCount: count(wire.failure_count),
    sourceFailureCount: count(wire.source_failure_count),
    documentFailureCount: count(wire.document_failure_count),
    needsPublishCount: count(wire.needs_publish_count),
    documentCoverage: coverage,
    analysisCoverage: Object.fromEntries(
      Object.entries(analysisCoverageWire).map(([key, item]) => [key, count(item)])
    ),
  };
};

export const fetchOperationsSummary = async (cycle?: string): Promise<OperationsSummary> => {
  const response = await api.get('/admin/ballot-initiatives/operations-summary', {
    params: { cycle: cycle || undefined },
  });
  return normalizeOperationsSummary(response.data.data);
};

export const fetchRunSummaries = async (cursor?: string): Promise<{ entries: ImportRunSummary[]; nextCursor: string | null; hasMore: boolean }> => {
  const response = await api.get('/admin/ballot-initiatives/import-runs/summaries', {
    params: { limit: 20, cursor: cursor || undefined },
  });
  const page = record(response.data.data) as PageWire<unknown>;
  return {
    entries: (page.entries ?? []).map(normalizeRun),
    nextCursor: page.next_cursor ?? null,
    hasMore: page.has_more === true,
  };
};

export const fetchRunDetail = async (runId: string): Promise<ImportRunDetail> => {
  const response = await api.get(`/admin/ballot-initiatives/import-runs/${runId}`);
  const wire = record(response.data.data);
  return {
    snapshots: Array.isArray(wire.snapshots) ? wire.snapshots : [],
    fetchAttempts: Array.isArray(wire.fetch_attempts) ? wire.fetch_attempts : [],
    documents: Array.isArray(wire.documents) ? wire.documents : [],
    warnings: Array.isArray(wire.warnings) ? wire.warnings : [],
    jobLogs: Array.isArray(wire.job_logs) ? wire.job_logs : [],
  };
};

export const fetchDocumentCoverage = async (
  cycle: string,
  search = '',
  cursor?: string,
  mode: ImportMode = 'live'
): Promise<{ entries: DocumentCoverageEntry[]; nextCursor: string | null; hasMore: boolean }> => {
  const response = await api.get('/admin/ballot-initiatives/document-coverage', {
    params: { cycle: cycle || undefined, mode, search: search || undefined, limit: 50, cursor: cursor || undefined },
  });
  const page = record(response.data.data) as PageWire<unknown>;
  return {
    entries: (page.entries ?? []).map(item => {
      const wire = record(item);
      const documents: Record<string, DocumentState> = {};
      Object.entries(record(wire.documents)).forEach(([key, state]) => { documents[key] = normalizeDocumentState(state); });
      return {
        initiativeId: text(wire.initiative_id), cycle: text(wire.initiative_cycle),
        initiativeNumber: text(wire.initiative_number), measureNumber: nullableText(wire.measure_number),
        popularName: nullableText(wire.popular_name), unofficialCaption: nullableText(wire.unofficial_caption),
        ballotTitle: nullableText(wire.ballot_title), status: text(wire.status), documents,
      };
    }),
    nextCursor: page.next_cursor ?? null,
    hasMore: page.has_more === true,
  };
};

export const fetchSourceChanges = async (
  cursor?: string,
  filters: { source?: string; disposition?: string } = {}
): Promise<{ entries: SourceChangeEntry[]; nextCursor: string | null; hasMore: boolean }> => {
  const response = await api.get('/admin/ballot-initiatives/source-changes', {
    params: { limit: 25, cursor: cursor || undefined, source: filters.source || undefined, disposition: filters.disposition || undefined },
  });
  const page = record(response.data.data) as PageWire<unknown>;
  return {
    entries: (page.entries ?? []).map(item => {
      const wire = record(item);
      return {
        id: text(wire.id), initiativeId: text(wire.initiative_id), sourceCode: text(wire.source_code),
        fieldPath: text(wire.field_path), changeType: text(wire.change_type), beforeValue: wire.before_value,
        afterValue: wire.after_value, disposition: text(wire.projection_disposition), cause: nullableText(wire.cause),
        operation: nullableText(wire.operation), mode: nullableText(wire.mode),
        visibility: text(wire.visibility), sourceUrl: nullableText(wire.final_url) || nullableText(wire.source_url), occurredAt: text(wire.changed_at),
      };
    }),
    nextCursor: page.next_cursor ?? null,
    hasMore: page.has_more === true,
  };
};

export const normalizeBulkPreview = (value: unknown): BulkPreview => {
  const wire = record(value);
  return {
    eligibleIdentityCount: count(wire.eligible_identity_count),
    manifestSha256: text(wire.manifest_sha256),
    resumedFromRunId: nullableText(wire.resumed_from_run_id),
    executionIdentityLimit: count(wire.execution_identity_limit),
    identitiesInNextExecution: count(wire.identities_in_next_execution),
    pdfsReused: count(wire.pdfs_reused),
    pdfsToAcquire: count(wire.pdfs_to_acquire),
    documentsSelected: count(wire.documents_selected),
  };
};
